import React from 'react'
import i18n from 'config/i18n'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { SubmissionState, SUBMISSION_VIEW_ENUM, PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { TaskState, EngagementId, LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { UrlCell, UrlCellProps } from 'components/Shared/MuiTable/Cell/UrlCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { CommentCell } from 'components/Shared/MuiTable/Cell/CommentCell'
import { MediaCell, MediaCellProps } from 'components/Shared/MuiTable/Cell/MediaCell'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { Country, PLATFORM_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { parseBackendDate } from 'services/dates'
import { TagContentType } from 'particles/Tag'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { GridColDefProps } from '../SimpleTable/constants'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { convertSecondsToTime } from 'components/utils'

export interface RowsProps {
    // Note: Row id is treated from MUI DataGridPro component as list key, don't use index for dynamic list key
    // issue related: https://gitlab.com/partipost-dev/engineering/backlog-items/-/issues/246
    id: string
    uuid: string
    socialMediaInfo: {
        profiles: SocialMediaAccount[]
    }
    insights_screenshot_files: string[]
    follower_count: {
        value: string
        color?: string
    }
    engagement_impression_count?: {
        value: string
        color?: string
    }
    engagement_like_count?: {
        value: string
        color?: string
    }
    engagement_reach_count?: {
        value: string
        color?: string
    }
    engagement_view_count?: {
        value: string
        color?: string
    }
    engagement_comment_count?: {
        value: string
        color?: string
    }
    engagement_tag_count?: {
        value: string
        color?: string
    }
    engagement_share_count?: {
        value: string
        color?: string
    }
    engagement_cta_click_count?: {
        value: string
        color?: string
    }
    engagement_save_count?: {
        value: string
        color?: string
    }
    engagement_total_watch_time_second_count?: {
        value: string
        color?: string
    }
    engagement_total_play_time_second_count?: {
        value: string
        color?: string
    }
    engagement_watched_full_video_percentage_count?: {
        value: string
        color?: string
    }
    engagement_average_watch_time_second_count?: {
        value: string
        color?: string
    }
    linkInBioUrl: string
    editsRequested: {
        submission?: SubmissionState
        contentType: TagContentType
    }
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
    lastReviewedBy: {
        value?: string
    }
    lastReviewedAt: {
        value?: string
    }
}

export const getColumns = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    showLinkInBio: boolean,
    engagementTypes: EngagementId[],
    task: TaskState,
    canManageTag: boolean,
): GridColDefProps[] => {
    const hasPlatform = (platform: TASK_TYPE_ENUM) => {
        return task.type.includes(platform)
    }

    if (viewType === 'edits_requested') {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    ...(hasPlatform(TASK_TYPE_ENUM.IG_POST) || hasPlatform(TASK_TYPE_ENUM.IG_REEL)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: i18n.t('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.FB_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: i18n.t('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.TIKTOK_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.XIAOHONGSHU_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                                  headerName: i18n.t('submission.table.XHSUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },
            {
                field: 'insights_screenshot_files',
                headerName: i18n.t('submission.table.engagementScreenshotLabel'),
                width: 220,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'follower_count',
                headerName: i18n.t('submission.table.followerCountLabel'),
                width: 240,
                sortable: false,
                filterable: false,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                renderHeader: getInfoTooltipHeaderCell({
                    width: '190px',
                    title: i18n.t('submission.table.followerCountLabel'),
                    description: i18n.t(
                        'submission.table.followerCountTooltip',
                        'The number Partiposters entered when submitting engagement result for the campaign',
                    ),
                }),
            },
            ...(engagementTypes.includes(EngagementId.LIKE)
                ? [
                      {
                          field: 'engagement_like_count',
                          headerName: i18n.t('submission.table.likesLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),

            ...(engagementTypes.includes(EngagementId.COMMENT)
                ? [
                      {
                          field: 'engagement_comment_count',
                          headerName: i18n.t('submission.table.commentsLabel'),
                          width: 120,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.VIEW)
                ? [
                      {
                          field: 'engagement_view_count',
                          headerName: i18n.t('submission.table.viewsLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.SHARE)
                ? [
                      {
                          field: 'engagement_share_count',
                          headerName: i18n.t('submission.table.sharesLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TAG)
                ? [
                      {
                          field: 'engagement_tag_count',
                          headerName: i18n.t('submission.table.tagsDateLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.REACH)
                ? [
                      {
                          field: 'engagement_reach_count',
                          headerName: i18n.t('submission.table.reachLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.IMPRESSION)
                ? [
                      {
                          field: 'engagement_impression_count',
                          headerName: i18n.t('submission.table.impressionLabel'),
                          width: 125,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.SAVE)
                ? [
                      {
                          field: 'engagement_save_count',
                          headerName: i18n.t('submission.table.savesLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.CTA_CLICK)
                ? [
                      {
                          field: 'engagement_cta_click_count',
                          headerName: i18n.t('submission.table.ctaButtonClicksLabel'),
                          width: 165,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TOTAL_WATCH_TIME)
                ? [
                      {
                          field: 'engagement_total_watch_time_second_count',
                          headerName: i18n.t('engagementMetric.totalWatchTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TOTAL_PLAY_TIME)
                ? [
                      {
                          field: 'engagement_total_play_time_second_count',
                          headerName: i18n.t('submission.campaignReport.metric.totalPlayTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.WATCHED_FULL_VIDEO)
                ? [
                      {
                          field: 'engagement_watched_full_video_percentage_count',
                          headerName: i18n.t('engagementMetric.watchedFullVideo'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.AVERAGE_WATCH_TIME)
                ? [
                      {
                          field: 'engagement_average_watch_time_second_count',
                          headerName: i18n.t('engagementMetric.averageWatchTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(showLinkInBio
                ? [
                      {
                          field: 'linkInBioUrl',
                          type: 'string',
                          headerName: i18n.t('submission.table.linkInBioLabel'),
                          width: 170,
                          renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            {
                field: 'lastReviewedBy',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'lastReviewedAt',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'editsRequested',
                type: 'string',
                headerName: i18n.t('submission.table.reasonEditRequestLabel'),
                width: 200,
                renderCell: (props: GridRenderCellParams) => <CommentCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 200,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    } else {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    ...(hasPlatform(TASK_TYPE_ENUM.IG_POST) || hasPlatform(TASK_TYPE_ENUM.IG_REEL)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: i18n.t('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.FB_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: i18n.t('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.TIKTOK_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.XIAOHONGSHU_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                                  headerName: i18n.t('submission.table.XHSUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },
            {
                field: 'insights_screenshot_files',
                headerName: i18n.t('submission.table.engagementScreenshotLabel'),
                width: 220,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'follower_count',
                headerName: i18n.t('submission.table.followerCountLabel'),
                width: 240,
                sortable: false,
                filterable: false,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                renderHeader: getInfoTooltipHeaderCell({
                    width: '190px',
                    title: i18n.t('submission.table.followerCountLabel'),
                    description: i18n.t(
                        'submission.table.followerCountTooltip',
                        'The number Partiposters entered when submitting engagement result for the campaign',
                    ),
                }),
            },
            ...(engagementTypes.includes(EngagementId.LIKE)
                ? [
                      {
                          field: 'engagement_like_count',
                          headerName: i18n.t('submission.table.likesLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),

            ...(engagementTypes.includes(EngagementId.COMMENT)
                ? [
                      {
                          field: 'engagement_comment_count',
                          headerName: i18n.t('submission.table.commentsLabel'),
                          width: 120,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.VIEW)
                ? [
                      {
                          field: 'engagement_view_count',
                          headerName: i18n.t('submission.table.viewsLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.SHARE)
                ? [
                      {
                          field: 'engagement_share_count',
                          headerName: i18n.t('submission.table.sharesLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TAG)
                ? [
                      {
                          field: 'engagement_tag_count',
                          headerName: i18n.t('submission.table.tagsDateLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.REACH)
                ? [
                      {
                          field: 'engagement_reach_count',
                          headerName: i18n.t('submission.table.reachLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.IMPRESSION)
                ? [
                      {
                          field: 'engagement_impression_count',
                          headerName: i18n.t('submission.table.impressionLabel'),
                          width: 125,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.SAVE)
                ? [
                      {
                          field: 'engagement_save_count',
                          headerName: i18n.t('submission.table.savesLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.CTA_CLICK)
                ? [
                      {
                          field: 'engagement_cta_click_count',
                          headerName: i18n.t('submission.table.ctaButtonClicksLabel'),
                          width: 165,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TOTAL_WATCH_TIME)
                ? [
                      {
                          field: 'engagement_total_watch_time_second_count',
                          headerName: i18n.t('engagementMetric.totalWatchTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TOTAL_PLAY_TIME)
                ? [
                      {
                          field: 'engagement_total_play_time_second_count',
                          headerName: i18n.t('submission.campaignReport.metric.totalPlayTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.WATCHED_FULL_VIDEO)
                ? [
                      {
                          field: 'engagement_watched_full_video_percentage_count',
                          headerName: i18n.t('engagementMetric.watchedFullVideo'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.AVERAGE_WATCH_TIME)
                ? [
                      {
                          field: 'engagement_average_watch_time_second_count',
                          headerName: i18n.t('engagementMetric.averageWatchTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(showLinkInBio
                ? [
                      {
                          field: 'linkInBioUrl',
                          type: 'string',
                          headerName: i18n.t('submission.table.linkInBioLabel'),
                          width: 200,
                          renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(viewType !== SUBMISSION_VIEW_ENUM.PENDING
                ? [
                      {
                          field: 'lastReviewedBy',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                      {
                          field: 'lastReviewedAt',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 160,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    }
}

export const getRows = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    task: TaskState,
    country: Country,
): RowsProps[] => {
    return submissions.map((submission: SubmissionState) => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter((account: any) => account.platform === PLATFORM_ENUM.IG)[0]
        const fbProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
        )[0]
        const tiktokProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
        )[0]
        const xiaohongshuProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
        )[0]
        let linkInBioUrl
        if (task && task.data) {
            if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK) {
                linkInBioUrl = task.data['link_in_bio_url']
            } else if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK) {
                if (submission.submission_task != undefined) {
                    linkInBioUrl = submission.submission_task.data['link_in_bio_url']
                }
            }
        }
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: {
                profile,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, xiaohongshuProfile],
            },
            insights_screenshot_files:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? submission && submission.data && submission.data.insights_screenshot_files
                    : submission && submission.reviewed_data && submission.reviewed_data.insights_screenshot_files,
            follower_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.follower_count }
                    : { value: submission && submission.reviewed_data && submission.reviewed_data.follower_count },
            engagement_impression_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_impression_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_impression_count,
                      },
            engagement_like_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_like_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_like_count,
                      },
            engagement_reach_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_reach_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_reach_count,
                      },
            engagement_view_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_view_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_view_count,
                      },
            engagement_comment_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_comment_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_comment_count,
                      },
            engagement_tag_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_tag_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_tag_count,
                      },
            engagement_share_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_share_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_share_count,
                      },
            engagement_cta_click_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_cta_click_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_cta_click_count,
                      },
            engagement_save_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_save_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_save_count,
                      },
            engagement_total_watch_time_second_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? {
                          value:
                              submission &&
                              submission.data &&
                              convertSecondsToTime(submission.data.engagement_total_watch_time_second_count),
                      }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              convertSecondsToTime(submission.reviewed_data.engagement_total_watch_time_second_count),
                      },
            engagement_total_play_time_second_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? {
                          value:
                              submission &&
                              submission.data &&
                              convertSecondsToTime(submission.data.engagement_total_play_time_second_count),
                      }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              convertSecondsToTime(submission.reviewed_data.engagement_total_play_time_second_count),
                      },
            engagement_watched_full_video_percentage_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? {
                          value:
                              submission &&
                              submission.data &&
                              `${submission.data.engagement_watched_full_video_percentage_count}%`,
                      }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              `${submission.reviewed_data.engagement_watched_full_video_percentage_count}%`,
                      },
            engagement_average_watch_time_second_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? {
                          value:
                              submission &&
                              submission.data &&
                              convertSecondsToTime(submission.data.engagement_average_watch_time_second_count),
                      }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              convertSecondsToTime(submission.reviewed_data.engagement_average_watch_time_second_count),
                      },
            linkInBioUrl: linkInBioUrl,
            lastReviewedBy: {
                value: submission.reviewed_by_system
                    ? i18n.t('statusLabel.autoApproved')
                    : submission.moved_out_of_pending_by?.name,
            },
            lastReviewedAt: {
                value: submission.moved_out_of_pending_at
                    ? parseBackendDate(submission.moved_out_of_pending_at, country, { showTime: true })
                    : undefined,
            },
            editsRequested:
                viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { submission: submission, contentType: 'warning' }
                    : { submission: undefined, contentType: 'warning' },
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
        }
    })
}
